import { ApiConnection } from '@send-frontend/lib/api';
import { NamedBlob } from '@send-frontend/types';
import JSZip from 'jszip';
import { MAX_FILE_SIZE } from './const';
import { trpc } from './trpc';

/**
 * Generates a SHA-256 hash from a file blob.
 *
 * @param {Blob} fileBlob - The file blob to hash
 * @returns {Promise<string>} - Returns a Promise that resolves to the hexadecimal hash string
 */
export async function generateFileHash(fileBlob: Blob): Promise<string> {
  // Small blobs (<= SPLIT_SIZE) only: reading the whole blob into one buffer is
  // safe here. Large files are hashed window-by-window via streamZippedParts,
  // which never materializes the whole file (and never slices at high offsets).
  const fileArrayBuffer = await fileBlob.arrayBuffer();
  return sha256Hex(fileArrayBuffer);
}

/**
 * Hex-encodes the SHA-256 digest of an already-in-memory buffer.
 */
export async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/*
 * Checks if user can continue uploading based on current storage usage and limit
 * @param {number} currentUploadSize - Size of the upload in bytes
 * @returns {boolean} - Returns true if user can upload, false otherwise
 */
export async function canUserUpload(
  currentUploadSize: number
): Promise<boolean> {
  // check if user would exceed storage limit with this upload
  const { active, limit } = await trpc.getTotalUsedStorage.query();
  return active + currentUploadSize >= limit;
}

/**
 * Returns a promise that resolves after an amount of time has passed.
 *
 * @param { number } delayMs - number of milliseconds that must pass before the promise resolves
 */
export function delay(delayMs = 100): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * Runs a function at an interval until it succeeds or the maximum wait time is reached.
 *
 * @param {function(): any} fn - The function to retry. Function can be `async`.
 * @param {number} waitTimeMs - How long to wait between each retry.
 * @param {number} maxWaitTimeMs - The maximum amount of time to retry until we give up.
 */
export async function retryUntilSuccessOrTimeout(
  fn: () => Promise<boolean>,
  waitTimeMs: number = 1_000,
  maxWaitTimeMs: number = 60_000
): Promise<void> {
  for (
    let waitTotalMs = 0;
    waitTotalMs < maxWaitTimeMs;
    waitTotalMs += waitTimeMs
  ) {
    await delay(waitTimeMs);
    try {
      const result: boolean = await fn();
      // Stop retrying if the function returns a truthy value
      if (!!result) return;
    } catch (e) {
      console.error(
        `Error on waiting for the file to show up in storage: ${e}`
      );
    }
  }
}

export function calculateTimeout(
  fileSizeMb: number,
  uploadSpeedMbps: number,
  buffer: number = 1.25
): number {
  const uploadSpeedMBps = uploadSpeedMbps / 8;
  const uploadTime = fileSizeMb / uploadSpeedMBps;
  return Math.ceil(uploadTime * buffer); // Returns the timeout in seconds with buffer
}

export async function streamToArrayBuffer(
  stream: ReadableStream,
  size?: number
): Promise<ArrayBufferLike> {
  const reader = stream.getReader();
  let state = await reader.read();

  if (size) {
    const result = new Uint8Array(size);
    let offset = 0;
    while (!state.done) {
      result.set(state.value, offset);
      offset += state.value.length;
      state = await reader.read();
    }
    return result.buffer;
  }

  const parts = [];
  let len = 0;
  while (!state.done) {
    parts.push(state.value);
    len += state.value.length;
    state = await reader.read();
  }
  let offset = 0;
  const result = new Uint8Array(len);
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result.buffer;
}

export function timestamp(): number {
  return new Date().getTime();
}

// https://gist.github.com/zentala/1e6f72438796d74531803cc3833c039c
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes == 0) return '0 Bytes';
  const k = 1024,
    dm = decimals,
    sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Zips `blob` under `filename` and returns the archive as a Blob.
 *
 * Why this is not just `zip.generateAsync({ type: 'blob' })`:
 * JSZip's one-shot blob output builds the entire archive as a single
 * `Uint8Array` and then calls `new Blob([thatArray])`. Firefox rejects any
 * single ArrayBuffer/ArrayBufferView Blob member larger than 2 GB with
 * "can't construct the Blob ... larger than 2 GB" — so zipping a file bigger
 * than ~2 GB (e.g. a typeless file wrapped by {@link formatBlob}) throws before
 * a single byte is uploaded (#981). Note this is a hard per-member limit, not an
 * out-of-memory condition: the same Firefox happily allocates a 6 GiB
 * ArrayBuffer, but refuses a >2 GB Blob member.
 *
 * Instead we consume JSZip's streaming output and hand the (individually
 * sub-2 GB) chunks to the Blob constructor as separate members. A Blob's *total*
 * size may exceed 2 GB as long as no single member does, so the archive can be
 * arbitrarily large.
 *
 * Backwards compatibility: `generateAsync` is itself implemented on top of this
 * same internal stream with the same default settings (STORE, no compression),
 * so the archive bytes are identical to the previous implementation. Files
 * zipped before and after this change are byte-for-byte interchangeable and the
 * download/unzip path is unaffected — this only changes how the output Blob is
 * assembled in memory, not its contents.
 */
export async function zipBlob(blob: Blob, filename: string): Promise<Blob> {
  const zip = new JSZip();
  zip.file(filename, blob);

  // Typed as BlobPart[] (not Uint8Array[]) so the chunks pass straight to the
  // Blob constructor without tripping the Uint8Array<ArrayBufferLike> vs
  // Uint8Array<ArrayBuffer> generic mismatch in newer lib.dom typings.
  const chunks: BlobPart[] = [];
  await new Promise<void>((resolve, reject) => {
    zip
      .generateInternalStream({ type: 'uint8array' })
      .on('data', (chunk: Uint8Array) => chunks.push(chunk as BlobPart))
      .on('error', reject)
      .on('end', () => resolve())
      .resume();
  });
  return new Blob(chunks, { type: 'application/zip' });
}

export async function unzipArrayBuffer(
  arrayBuffer: ArrayBufferLike
): Promise<ArrayBuffer> {
  try {
    const zip = new JSZip();
    // Convert ArrayBufferLike to ArrayBuffer if needed
    const buffer =
      arrayBuffer instanceof ArrayBuffer
        ? arrayBuffer
        : new ArrayBuffer(arrayBuffer.byteLength);
    if (!(arrayBuffer instanceof ArrayBuffer)) {
      new Uint8Array(buffer).set(new Uint8Array(arrayBuffer));
    }

    const zipData = await zip.loadAsync(buffer);

    // Get the first file from the zip (assuming single file per zip for this implementation)
    const fileNames = Object.keys(zipData.files);
    if (fileNames.length === 0) {
      throw new Error('No files found in zip archive');
    }

    // Take the first file (or we could implement logic to find the main file)
    const fileName = fileNames[0];
    const file = zipData.files[fileName];

    if (file.dir) {
      throw new Error('Expected file but found directory in zip archive');
    }

    // Extract the file content as ArrayBuffer
    const content = await file.async('arraybuffer');
    return content;
  } catch (error) {
    console.error('Error unzipping content:', error);
    // If unzipping fails, return the original content (might not be zipped)
    // Convert ArrayBufferLike to ArrayBuffer for consistent return type
    if (arrayBuffer instanceof ArrayBuffer) {
      return arrayBuffer;
    } else {
      const buffer = new ArrayBuffer(arrayBuffer.byteLength);
      new Uint8Array(buffer).set(new Uint8Array(arrayBuffer));
      return buffer;
    }
  }
}

export async function unzipMultipartPiece(
  arrayBuffer: ArrayBufferLike
): Promise<{
  content: ArrayBuffer;
  isMultipart: boolean;
  partNumber?: number;
}> {
  try {
    const zip = new JSZip();
    // Convert ArrayBufferLike to ArrayBuffer if needed
    const buffer =
      arrayBuffer instanceof ArrayBuffer
        ? arrayBuffer
        : new ArrayBuffer(arrayBuffer.byteLength);
    if (!(arrayBuffer instanceof ArrayBuffer)) {
      new Uint8Array(buffer).set(new Uint8Array(arrayBuffer));
    }

    const zipData = await zip.loadAsync(buffer);

    // Get the files from the zip
    const fileNames = Object.keys(zipData.files);
    if (fileNames.length === 0) {
      throw new Error('No files found in zip archive');
    }

    // Look for multipart file or take the first file
    const targetFileName = fileNames[0];
    const isMultipart = false;

    const file = zipData.files[targetFileName];

    if (file.dir) {
      throw new Error('Expected file but found directory in zip archive');
    }

    // Extract the file content as ArrayBuffer
    const content = await file.async('arraybuffer');
    return { content, isMultipart, partNumber: undefined };
  } catch (error) {
    console.error('Error unzipping multipart content:', error);
    // If unzipping fails, return the original content (might not be zipped)
    const buffer =
      arrayBuffer instanceof ArrayBuffer
        ? arrayBuffer
        : new ArrayBuffer(arrayBuffer.byteLength);
    if (!(arrayBuffer instanceof ArrayBuffer)) {
      new Uint8Array(buffer).set(new Uint8Array(arrayBuffer));
    }
    return { content: buffer, isMultipart: false };
  }
}

export const formatBlob = async (blob: NamedBlob): Promise<NamedBlob> => {
  if (blob.type === '') {
    // Typeless files are wrapped in a whole-file zip here, before doUpload. For
    // files > ~2 GB this used to throw "can't construct the Blob" (#981); it now
    // works because zipBlob assembles its output from sub-2 GB chunks. Wrapping
    // the resulting (possibly >2 GB) Blob in another Blob is fine — the 2 GB cap
    // is per ArrayBuffer/View member, not per Blob member.
    const zippedBlob = await zipBlob(blob, blob.name);
    const compressedBlob = new Blob([zippedBlob], {
      type: 'application/zip',
    }) as NamedBlob;
    compressedBlob.name = `${blob.name}.zip`;
    return compressedBlob;
  }
  return blob;
};

export const splitIntoMultipleZips = async (
  blob: NamedBlob,
  maxSize: number
): Promise<NamedBlob[]> => {
  const chunks: NamedBlob[] = [];

  // If the blob is smaller than maxSize, return it as a single zip
  if (blob.size <= maxSize) {
    const singleZip = await zipBlob(blob, blob.name);
    const zippedBlob = new Blob([singleZip], {
      type: 'application/zip',
    }) as NamedBlob;
    zippedBlob.name = `${blob.name}`;
    return [zippedBlob];
  }

  // Split the blob into chunks
  const totalSize = blob.size;
  const numChunks = Math.ceil(totalSize / maxSize);

  for (let i = 0; i < numChunks; i++) {
    const start = i * maxSize;
    const end = Math.min(start + maxSize, totalSize);

    // Create a chunk from the original blob
    const chunk = blob.slice(start, end);
    const chunkBlob = new Blob([chunk], { type: blob.type }) as NamedBlob;
    chunkBlob.name = `${blob.name}`;

    // Zip the chunk
    const zippedChunk = await zipBlob(chunkBlob, chunkBlob.name);
    const zippedBlob = new Blob([zippedChunk], {
      type: 'application/zip',
    }) as NamedBlob;
    zippedBlob.name = `${chunkBlob.name}`;

    chunks.push(zippedBlob);
  }

  return chunks;
};

/**
 * Checks a precomputed file hash against the suspicious-files list and blocks
 * the upload (alert + throw) if it matches.
 */
const assertNotSuspicious = async (api: ApiConnection, fileHash: string) => {
  const { isSuspicious } = await api.call<{ isSuspicious: boolean }>(
    `uploads/check-upload-hash/${fileHash}`
  );

  if (isSuspicious) {
    alert(
      'Warning: This file has been reported as suspicious. You cannot upload it. If you believe this is an error, please contact support.'
    );
    throw new Error('Suspicious file detected');
  }
};

const hashAndCheck = async (api: ApiConnection, fileBlob: NamedBlob) => {
  // generate a hash from the file
  const fileHash = await generateFileHash(fileBlob);
  console.log('File hash (SHA-256):', fileHash);
  await assertNotSuspicious(api, fileHash);
  return fileHash;
};

export const hashFiles = async (
  api: ApiConnection,
  fileBlob: NamedBlob,
  maxSize: number,
  // Optional callback invoked as each chunk is hashed/scanned, so callers can
  // surface progress during this phase — for large files it reads and hashes
  // the whole file and can take several seconds before any bytes upload (#968).
  onProgress?: (completed: number, total: number) => void
): Promise<string[]> => {
  const hashFromChunk: string[] = [];

  // If the blob is smaller than maxSize, return it as a single zip
  if (fileBlob.size <= maxSize) {
    const hashedBlob = await hashAndCheck(api, fileBlob);
    onProgress?.(1, 1);
    return [hashedBlob];
  }

  // Split the blob into chunks
  const totalSize = fileBlob.size;
  const numChunks = Math.ceil(totalSize / maxSize);

  for (let i = 0; i < numChunks; i++) {
    const start = i * maxSize;
    const end = Math.min(start + maxSize, totalSize);

    // Create a chunk from the original blob
    const chunk = fileBlob.slice(start, end);
    const chunkBlob = new Blob([chunk], { type: fileBlob.type }) as NamedBlob;
    chunkBlob.name = `${fileBlob.name}`;

    // hash the chunk
    const zippedChunk = await hashAndCheck(api, chunkBlob);

    hashFromChunk.push(zippedChunk);
    onProgress?.(i + 1, numChunks);
  }
  return hashFromChunk;
};

/**
 * A single multipart piece ready to upload: the zipped chunk blob plus the
 * SHA-256 hash of its *raw* (pre-zip) bytes, matching the per-chunk hash the
 * backend records via the `uploads` create-entry call.
 */
export type UploadPart = { blob: NamedBlob; hash: string };

/**
 * Streams a large file into upload-ready parts, one `maxSize` window at a time.
 *
 * Crucially, it reads the file **sequentially from offset 0 via
 * `fileBlob.stream()`** and never calls `.slice()`/`.arrayBuffer()` at a high
 * byte offset. The previous approach (hashFiles + splitIntoMultipleZips) sliced
 * the file at offsets past ~2 GiB, which Firefox rejects with a NotReadableError
 * / "can't construct the Blob" on files larger than ~2 GB (#981). Reading via a
 * ReadableStream keeps each in-memory buffer bounded to one window and avoids
 * the 2^31 offset boundary entirely.
 *
 * Parts are yielded **in order as they become ready**, so a caller can begin
 * uploading window 0 while later windows are still being read/hashed/zipped
 * (#980).
 *
 * For each window it hashes the raw bytes, runs the suspicious-file check, then
 * zips the window — producing the exact same wire format as
 * splitIntoMultipleZips (a zip of the raw chunk, named after the original file).
 */
export async function* streamZippedParts(
  api: ApiConnection,
  fileBlob: NamedBlob,
  maxSize: number,
  // Invoked with cumulative bytes hashed so callers can show hashing progress.
  onBytesHashed?: (bytesHashed: number) => void
): AsyncGenerator<UploadPart, void, unknown> {
  const reader = fileBlob.stream().getReader();
  let windowChunks: Uint8Array[] = [];
  let windowSize = 0;
  let totalHashed = 0;

  // Turn the accumulated (<= maxSize) window into an upload-ready part.
  const flushWindow = async (): Promise<UploadPart> => {
    const windowBytes = new Uint8Array(windowSize);
    let offset = 0;
    for (const piece of windowChunks) {
      windowBytes.set(piece, offset);
      offset += piece.byteLength;
    }
    windowChunks = [];
    windowSize = 0;

    const hash = await sha256Hex(windowBytes.buffer);
    await assertNotSuspicious(api, hash);

    const rawBlob = new Blob([windowBytes], {
      type: fileBlob.type,
    }) as NamedBlob;
    rawBlob.name = fileBlob.name;
    const zipped = await zipBlob(rawBlob, fileBlob.name);
    const zippedBlob = new Blob([zipped], {
      type: 'application/zip',
    }) as NamedBlob;
    zippedBlob.name = fileBlob.name;

    return { blob: zippedBlob, hash };
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      let chunk = value;
      // A single stream read can straddle one or more window boundaries.
      while (windowSize + chunk.byteLength >= maxSize) {
        const take = maxSize - windowSize;
        windowChunks.push(chunk.subarray(0, take));
        windowSize += take;
        totalHashed += take;
        onBytesHashed?.(totalHashed);
        yield await flushWindow();
        chunk = chunk.subarray(take);
      }
      if (chunk.byteLength > 0) {
        windowChunks.push(chunk);
        windowSize += chunk.byteLength;
      }
    }

    // Trailing partial window.
    if (windowSize > 0) {
      totalHashed += windowSize;
      onBytesHashed?.(totalHashed);
      yield await flushWindow();
    }
  } finally {
    reader.releaseLock();
  }
}

export const checkBlobSize = async (blob: NamedBlob) => {
  console.log(blob);
  if (blob.size > MAX_FILE_SIZE) {
    console.warn('File too big');
    return false;
  }
  return true;
};

export const getDaysUntilDate = (date: Date | string): number => {
  const targetDate = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();
  const result = Math.ceil(diff / (1000 * 3600 * 24));
  return !result || result < 0 ? 0 : result;
};

export const getAccessLinkWithoutPasswordHash = (link: string): string => {
  if (link.includes('#')) {
    return link.split('#')[0];
  }
  return link;
};

export type ExpirationOption =
  | 'never'
  | '24hours'
  | '14days'
  | '30days'
  | 'custom';

export function getExpirationDate(
  selectedExpiration: ExpirationOption,
  customDateTime: string
): string | undefined {
  const now = new Date();

  switch (selectedExpiration) {
    case 'never':
      return undefined;
    case '24hours':
      now.setHours(now.getHours() + 24);
      return now.toISOString();
    case '14days':
      now.setDate(now.getDate() + 14);
      return now.toISOString();
    case '30days':
      now.setDate(now.getDate() + 30);
      return now.toISOString();
    case 'custom':
      // datetime-local input provides value in format: YYYY-MM-DDTHH:mm
      if (!customDateTime) return undefined;
      try {
        const date = new Date(customDateTime);
        return date.toISOString();
      } catch (error) {
        console.error('Failed to parse custom date/time:', error);
        return undefined;
      }
    default:
      return undefined;
  }
}
